/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
import React from "react";
import { Button, Grid, TextField } from "@material-ui/core";
import { FormInputLabel } from "../components/common/FormInputLabel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as payment from "../constants/payment";
import { MenuItem } from "@material-ui/core";
import { Controller } from "react-hook-form";
import { useState } from "react";
import { useResendForm } from "./../hooks/use-resend-form";
import { CancelOutlined, CheckCircleOutlined } from "@material-ui/icons";
import { green, red } from "@material-ui/core/colors";
import Typography from "@material-ui/core/Typography";
import { useLocation } from "react-router-dom";

interface Field {
  id: string;
  label: string;
}
interface FieldArray extends Array<Field> {}

interface PaymentMethod {
  id: string;
  label: string;
  fields: FieldArray;
}

const schema = yup.object().shape({
  paymentMethod: yup.string().required("Campo requerido"),
  orderNumber: yup.string().required("Campo requerido"),
});

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ResendPayInfo = () => {
  const query = useQuery();

  const {
    submitResendPaymentForm,
    setShowMessage,
    showMessage,
    isPaymentApproved,
    setIsPaymentApproved,
  } = useResendForm();

  const { PAYMENT_METHODS } = payment;

  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0].id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmitHandler = (data: any) => {
    submitResendPaymentForm(data);
  };

  const paymentFields = React.useMemo(() => {
    const fields =
      PAYMENT_METHODS.find((e: PaymentMethod) => e.id === paymentMethod)
        ?.fields || [];
    return [...fields];
  }, [PAYMENT_METHODS, paymentMethod]);

  return (
    <>
      {showMessage && (
        <Grid
          container
          spacing={4}
          direction="column"
          alignItems="center"
          justify="center"
          style={{ minHeight: "40vh" }}>
          {isPaymentApproved && (
            <CheckCircleOutlined
              style={{ color: green[500] }}
              fontSize="large"
            />
          )}
          {!isPaymentApproved && (
            <CancelOutlined style={{ color: red[500] }} fontSize="large" />
          )}
          <Typography variant="subtitle2">
            {isPaymentApproved
              ? "Solicitud enviada exitosamente"
              : "Su petición no pudo ser atendida, revise el estado del pedido"}
          </Typography>
          <Typography
            color="primary"
            variant="button"
            onClick={() => {
              setShowMessage(!showMessage);
              setIsPaymentApproved(!isPaymentApproved);
            }}>
            <u className="pointer"> Hacer otra solicitud</u>
          </Typography>
        </Grid>
      )}
      {!showMessage && (
        <Grid
          container
          item
          direction={"column"}
          alignItems="stretch"
          xs={12}
          md={4}>
          <Grid item xs={12}>
            <h3>
              Completa todos los campos para solicitar el reenvío de una orden
              de compra:
            </h3>
          </Grid>
          <Grid item xs={12}>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
              <FormInputLabel text={"Número de orden"} />
              <TextField
                {...register("orderNumber")}
                type="text"
                margin="dense"
                variant="outlined"
                defaultValue={query.get("order")}
                size="small"
                placeholder="Ejemplo: 0123456"
                fullWidth
                required
              />

              <p className="helperError">{errors.orderNumber?.message}</p>

              <Controller
                control={control}
                name="paymentMethod"
                defaultValue={paymentMethod}
                render={({ field: { onChange, onBlur, ref } }) => (
                  <TextField
                    select
                    required
                    value={paymentMethod}
                    variant="outlined"
                    size="small"
                    onBlur={onBlur}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setPaymentMethod(event.target.value);
                      onChange(event);
                    }}
                    inputRef={ref}
                    fullWidth
                    error={errors.paymentMethod ? true : false}
                    helperText={errors.paymentMethod?.message}>
                    {PAYMENT_METHODS.map((e: PaymentMethod) => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <FormInputLabel text={"Parámetro (información de pedido)"} />
              {paymentFields.map((field: Field) => (
                <TextField
                  {...register(field.id)}
                  key={field.id}
                  type="text"
                  margin="dense"
                  variant="outlined"
                  size="small"
                  placeholder={field.label}
                  fullWidth
                  required
                  error={errors[field.id] ? true : false}
                  helperText={errors[field.id]?.message}></TextField>
              ))}

              <Button className="resend-button" type="submit">
                Enviar Solicitud
              </Button>
            </form>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ResendPayInfo;
